import * as React from 'react'
import {
  CalendarStateContext,
  RangeCalendarStateContext,
  Select,
  SelectValue,
  Button as AriaButton,
  Popover,
  ListBox,
  ListBoxItem,
  useLocale,
} from 'react-aria-components'
import { CaretDown, Check } from '@phosphor-icons/react'

/**
 * CalendarMonthYearSelect — month + year pickers for a calendar header, so users
 * can jump months/years without clicking the arrows. Reads the calendar's
 * react-aria state (works inside both `Calendar` and `RangeCalendar`) and moves
 * the focused date directly. Opt-in via each calendar's `monthYearSelect` prop.
 *
 * Built on react-aria's `Select` (not native `<select>`) so the dropdown matches
 * the DS menu style — white panel, gray-200 border, `shadow-fbui-lg`, small
 * gray-100-highlight items with a basil ✓ on the selection — and stays a
 * scrollable in-library overlay (safe nested inside the DatePicker popover).
 *
 * Year range comes from `minValue`/`maxValue` when set, else the focused year
 * ±10 — pass min/max on the calendar to widen it (e.g. a birthday picker).
 */

const triggerClass =
  'inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-sm font-semibold text-fbui-text-title outline-none transition-colors data-[hovered]:bg-fbui-gray-100 data-[pressed]:bg-fbui-gray-100 data-[focus-visible]:ring-2 data-[focus-visible]:ring-fbui-ring-brand'

const popoverClass =
  'z-50 overflow-hidden rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-1.5 shadow-fbui-lg outline-none'

const listBoxClass =
  'fbui-scroll max-h-60 min-w-32 overflow-y-auto outline-none'

const itemClass =
  'flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-xs text-fbui-gray-700 outline-none transition-colors data-[focused]:bg-fbui-gray-100 data-[focused]:text-fbui-gray-900 data-[selected]:font-medium data-[selected]:text-fbui-basil-700'

interface HeaderSelectProps {
  label: string
  selectedKey: number
  onSelect: (value: number) => void
  options: { id: number; label: string }[]
}

const HeaderSelect = ({
  label,
  selectedKey,
  onSelect,
  options,
}: HeaderSelectProps) => (
  <Select
    aria-label={label}
    selectedKey={selectedKey}
    onSelectionChange={(key) => onSelect(Number(key))}
  >
    <AriaButton className={triggerClass}>
      <SelectValue />
      <CaretDown
        weight="bold"
        className="size-3 text-fbui-gray-400"
        aria-hidden="true"
      />
    </AriaButton>
    <Popover offset={6} className={popoverClass}>
      <ListBox className={listBoxClass}>
        {options.map((opt) => (
          <ListBoxItem
            key={opt.id}
            id={opt.id}
            textValue={opt.label}
            className={itemClass}
          >
            {({ isSelected }) => (
              <>
                <span className="flex-1 truncate">{opt.label}</span>
                {isSelected && (
                  <Check
                    weight="bold"
                    className="size-3.5 shrink-0 text-fbui-basil-700"
                    aria-hidden="true"
                  />
                )}
              </>
            )}
          </ListBoxItem>
        ))}
      </ListBox>
    </Popover>
  </Select>
)

export const CalendarMonthYearSelect = () => {
  const calState = React.useContext(CalendarStateContext)
  const rangeState = React.useContext(RangeCalendarStateContext)
  const { locale } = useLocale()
  // Month names in the active locale (from the nearest `I18nProvider`, else the
  // browser default), recomputed only when the locale changes.
  const months = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { month: 'long' }).format(
          new Date(2021, i, 1)
        )
      ),
    [locale]
  )
  const state = calState ?? rangeState
  if (!state) return null

  const focused = state.focusedDate
  const minYear = state.minValue ? state.minValue.year : focused.year - 10
  const maxYear = state.maxValue ? state.maxValue.year : focused.year + 10
  const years: { id: number; label: string }[] = []
  for (let y = minYear; y <= maxYear; y++)
    years.push({ id: y, label: String(y) })

  return (
    <div className="flex items-center gap-1">
      <HeaderSelect
        label="Month"
        selectedKey={focused.month}
        onSelect={(month) => state.setFocusedDate(focused.set({ month }))}
        options={months.map((label, i) => ({ id: i + 1, label }))}
      />
      <HeaderSelect
        label="Year"
        selectedKey={focused.year}
        onSelect={(year) => state.setFocusedDate(focused.set({ year }))}
        options={years}
      />
    </div>
  )
}
CalendarMonthYearSelect.displayName = 'CalendarMonthYearSelect'
